import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2, Star } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AccountRow } from "./AccountRow";

const EMPTY = { label: "Home", name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", is_default: false };

function AddressForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-3 border border-border rounded-lg p-4 bg-slate-50/60" data-testid="address-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Label</Label><Input data-testid="addr-label" value={f.label} onChange={set("label")} placeholder="Home / Office" /></div>
        <div className="space-y-1"><Label className="text-xs">Full name</Label><Input data-testid="addr-name" value={f.name} onChange={set("name")} /></div>
      </div>
      <div className="space-y-1"><Label className="text-xs">Phone</Label><Input data-testid="addr-phone" value={f.phone} onChange={set("phone")} /></div>
      <div className="space-y-1"><Label className="text-xs">Address line 1</Label><Input data-testid="addr-line1" value={f.line1} onChange={set("line1")} /></div>
      <div className="space-y-1"><Label className="text-xs">Address line 2 (optional)</Label><Input data-testid="addr-line2" value={f.line2} onChange={set("line2")} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1"><Label className="text-xs">City</Label><Input data-testid="addr-city" value={f.city} onChange={set("city")} /></div>
        <div className="space-y-1"><Label className="text-xs">State</Label><Input data-testid="addr-state" value={f.state} onChange={set("state")} /></div>
        <div className="space-y-1"><Label className="text-xs">PIN code</Label><Input data-testid="addr-pincode" value={f.pincode} onChange={set("pincode")} /></div>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <Switch checked={f.is_default} onCheckedChange={(v) => setF({ ...f, is_default: v })} data-testid="addr-default" />
        Set as default address
      </label>
      <div className="flex gap-2 pt-1">
        <button data-testid="addr-save-btn" onClick={() => onSave(f)} disabled={saving || !f.name || !f.line1 || !f.city}
          className="h-9 px-4 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save address"}
        </button>
        <button onClick={onCancel} className="h-9 px-4 rounded-md border border-border text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
      </div>
    </div>
  );
}

export function AddressesDialog() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/me/addresses"); setList(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (open) load(); }, [open]);

  const save = async (f) => {
    setSaving(true);
    try {
      if (editId) await api.put(`/me/addresses/${editId}`, f);
      else await api.post("/me/addresses", f);
      toast.success(editId ? "Address updated" : "Address added");
      setAdding(false); setEditId(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/me/addresses/${id}`); toast.success("Address removed"); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setAdding(false); setEditId(null); }}>
      <DialogTrigger asChild>
        <button className="w-full" data-testid="open-addresses-dialog">
          <AccountRow icon={MapPin} title="Addresses" value={list.length ? `${list.length} saved` : "Add a delivery address"} testid="account-row-addresses" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="addresses-dialog">
        <DialogHeader><DialogTitle>Saved Addresses</DialogTitle></DialogHeader>
        {loading ? <p className="text-sm text-slate-400 py-6 text-center">Loading…</p> : (
          <div className="space-y-3">
            {list.length === 0 && !adding && (
              <div className="text-center py-6" data-testid="no-addresses">
                <MapPin className="h-8 w-8 text-slate-300 mx-auto" />
                <p className="text-sm text-slate-500 mt-2">No addresses saved yet.</p>
              </div>
            )}
            {list.map((a) => (
              editId === a.id ? (
                <AddressForm key={a.id} initial={a} onSave={save} onCancel={() => setEditId(null)} saving={saving} />
              ) : (
                <div key={a.id} className="border border-border rounded-lg p-4 flex items-start justify-between gap-3" data-testid={`address-${a.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-navy text-sm flex items-center gap-2">
                      {a.label} {a.is_default && <span className="inline-flex items-center gap-1 text-[10px] text-gold"><Star className="h-3 w-3 fill-gold" />Default</span>}
                    </p>
                    <p className="text-sm text-slate-600 mt-0.5">{a.name}{a.phone ? ` · ${a.phone}` : ""}</p>
                    <p className="text-sm text-slate-500">{[a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(", ")}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => { setEditId(a.id); setAdding(false); }} data-testid={`edit-address-${a.id}`} className="h-8 w-8 rounded-md border border-border flex items-center justify-center text-slate-500 hover:text-navy"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(a.id)} data-testid={`delete-address-${a.id}`} className="h-8 w-8 rounded-md border border-red-200 flex items-center justify-center text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              )
            ))}
            {adding ? (
              <AddressForm onSave={save} onCancel={() => setAdding(false)} saving={saving} />
            ) : (
              <button onClick={() => { setAdding(true); setEditId(null); }} data-testid="add-address-btn"
                className="w-full h-10 rounded-md border border-dashed border-border flex items-center justify-center gap-2 text-sm font-semibold text-navy hover:border-gold/50 hover:text-gold transition-colors">
                <Plus className="h-4 w-4" /> Add new address
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
